"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ClipboardList, ArrowRight, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const checklistItems = [{
  id: "item-1",
  question: "Is the bull new or used?",
  category: "Equipment Status"
}, {
  id: "item-2",
  question: "Is the inflatable included?",
  category: "Air Cushion"
}, {
  id: "item-3",
  question: "Is the blower included?",
  category: "Air Supply"
}, {
  id: "item-4",
  question: "Which controller comes with the machine?",
  category: "Electronics"
}, {
  id: "item-5",
  question: "What is included in the advertised price?",
  category: "Pricing & Scope"
}, {
  id: "item-6",
  question: "What are the assembled dimensions?",
  category: "Site Clearance"
}, {
  id: "item-7",
  question: "What are the product-specific power requirements?",
  category: "Electrical Specs"
}, {
  id: "item-8",
  question: "What condition information is available?",
  category: "Inspection"
}, {
  id: "item-9",
  question: "Has a used inflatable had visible repairs or patches?",
  category: "Material Integrity"
}, {
  id: "item-10",
  question: "Are compatible replacement parts available?",
  category: "Maintenance"
}, {
  id: "item-11",
  question: "What delivery information applies to the complete package?",
  category: "Freight Logistics"
}, {
  id: "item-12",
  question: "What warranty or support applies, if any?",
  category: "Coverage"
}];
export default function Checklist() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.05
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="prepurchasechecklist" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="font-mono text-xs uppercase tracking-widest px-2.5 py-1 rounded-none border-border bg-muted/60 text-foreground">
              <ClipboardList className="size-3.5 mr-1 text-primary" />
              PRE-PURCHASE CHECKLIST
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground uppercase">
            What to Check Before Buying an Inflatable Mechanical Bull
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            A useful comparison starts with the exact package rather than assumptions based on a product photo. Before buying, confirm the information that affects setup, operation, condition and ownership.
          </p>
        </div>

        {/* 12-Item Bordered Checklist Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {checklistItems.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between p-5 bg-card text-card-foreground border border-border shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-sm">
              <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                    {(index + 1).toString().padStart(2, "0")} / {item.category}
                  </span>
                  <span className="inline-flex size-6 items-center justify-center bg-primary text-primary-foreground">
                    <Check className="size-3.5 stroke-[2.5]" />
                  </span>
                </div>

                <p className="font-sans text-base font-semibold text-foreground leading-snug">
                  {item.question}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Mandatory Closing Statement Card */}
        <div className="mt-12 lg:mt-16 p-6 sm:p-8 bg-muted border-l-4 border-l-primary border-y border-r border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="size-10 bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-0.5">
              <ShieldAlert className="size-5" />
            </div>
            <div>
              <p className="font-sans text-base sm:text-lg font-medium text-foreground leading-relaxed">
                These questions give buyers a clearer picture of the complete system than a product photo or headline price alone.
              </p>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-3">
            <Button asChild variant="default" className="rounded-none font-mono uppercase text-xs tracking-wider">
              <Link to="/RequestQuotePage">
                <span>Request Product Information</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}